import React, { useState, useEffect } from "react";
import axios from "axios";
import { Image, Link, X, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageSelector = ({ value, onChange, label = "Image" }) => {
  const [showGallery, setShowGallery] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [urlInput, setUrlInput] = useState(value || "");
  const [activeTab, setActiveTab] = useState("gallery");

  useEffect(() => {
    setUrlInput(value || "");
  }, [value]);

  const fetchGalleryImages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gallery`);
      // Filter only images (not videos)
      const images = response.data.filter(item => item.media_type === "image");
      setGalleryImages(images);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenGallery = () => {
    setShowGallery(true);
    fetchGalleryImages();
  };

  const handleSelectFromGallery = (imageUrl) => {
    // Convert relative URL to full URL if needed
    const fullUrl = imageUrl.startsWith("http") 
      ? imageUrl 
      : `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
    onChange(fullUrl);
    setUrlInput(fullUrl);
    setShowGallery(false);
  };

  const handleUrlSubmit = () => {
    onChange(urlInput);
    setShowGallery(false);
  };

  const handleClear = () => {
    onChange("");
    setUrlInput("");
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {/* Preview and Controls */}
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            value={urlInput}
            onChange={(e) => {
              setUrlInput(e.target.value);
              onChange(e.target.value);
            }}
            placeholder="https://... ou sélectionner de la galerie"
            className="w-full"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleOpenGallery}
          className="shrink-0"
        >
          <Image className="h-4 w-4 mr-2" />
          Galerie
        </Button>
        {value && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleClear}
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Image Preview */}
      {value && (
        <div className="relative w-full h-32 rounded-lg overflow-hidden bg-muted border">
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/400x200?text=Image+non+disponible";
            }}
          />
          <div className="absolute top-2 right-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={handleClear}
              className="h-7 px-2"
            >
              <X className="h-3 w-3 mr-1" />
              Retirer
            </Button>
          </div>
        </div>
      )}

      {/* Gallery Dialog */}
      <Dialog open={showGallery} onOpenChange={setShowGallery}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Sélectionner une image</DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="gallery" className="gap-2">
                <Image className="h-4 w-4" />
                Galerie
              </TabsTrigger>
              <TabsTrigger value="url" className="gap-2">
                <Link className="h-4 w-4" />
                URL externe
              </TabsTrigger>
            </TabsList>

            <TabsContent value="gallery" className="mt-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : galleryImages.length === 0 ? (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <Image className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Aucune image dans la galerie
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Ajoutez des images dans la section Galerie de l'administration
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-[50vh] overflow-y-auto p-1">
                  {galleryImages.map((image) => {
                    const imageUrl = image.file_url.startsWith("http")
                      ? image.file_url
                      : `${process.env.REACT_APP_BACKEND_URL}${image.file_url}`;
                    const isSelected = value === imageUrl;
                    
                    return (
                      <button
                        key={image.id}
                        type="button"
                        onClick={() => handleSelectFromGallery(image.file_url)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:opacity-90 ${
                          isSelected
                            ? "border-primary ring-2 ring-primary/30"
                            : "border-transparent hover:border-muted-foreground/30"
                        }`}
                      >
                        <img
                          src={imageUrl}
                          alt={image.title?.fr || "Gallery image"}
                          className="w-full h-full object-cover"
                        />
                        {isSelected && (
                          <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                            <div className="bg-primary text-primary-foreground rounded-full p-1">
                              <Check className="h-4 w-4" />
                            </div>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <p className="text-white text-xs truncate">
                            {image.title?.fr || "Sans titre"}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="url" className="mt-4">
              <div className="space-y-4">
                <div>
                  <Label>URL de l'image</Label>
                  <Input
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="mt-1"
                  />
                </div>
                
                {urlInput && (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden bg-muted border">
                    <img
                      src={urlInput}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/400x200?text=URL+invalide";
                      }}
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowGallery(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="button"
                    onClick={handleUrlSubmit}
                    disabled={!urlInput}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Utiliser cette URL
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageSelector;
